import React, { useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { api } from "../../api";
import { useApi } from "../../hooks";

interface CouponDownloadModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  appliedFilter?: string;
  onlyFailed?: boolean;
  uniqueResults?: boolean;
  notApplicable?: string;
  confirmedFailure?: string;
  systemError?: string;
  checkoutTechnology?: string;
  merchantName?: string;
  merchantUrl?: string;
  startDate?: string;
  endDate?: string;
  publishedStatus?: string;
  runType?: string;
}

export const CouponDownloadModal: React.FC<CouponDownloadModalProps> = ({
  show,
  onHide,
  onSuccess,
  appliedFilter,
  onlyFailed,
  uniqueResults,
  notApplicable,
  confirmedFailure,
  systemError,
  checkoutTechnology,
  merchantName,
  merchantUrl,
  startDate: filterStartDate,
  endDate: filterEndDate,
  publishedStatus,
  runType,
}) => {
  const { get } = useApi();
  const [hoursBack, setHoursBack] = useState<number>(24);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateSql, setGenerateSql] = useState<boolean>(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setError(null);

      // Calculate start date (hours back from now, or use filter if provided)
      const now = new Date();
      const startDate = filterStartDate
        ? new Date(filterStartDate)
        : new Date(now.getTime() - hoursBack * 60 * 60 * 1000);

      // Fetch CSV directly from backend endpoint
      const params = new URLSearchParams();
      params.append("startDate", startDate.toISOString());

      // Add endDate if filter is provided
      if (filterEndDate) {
        params.append("endDate", new Date(filterEndDate).toISOString());
      }

      // Add all other filters
      if (appliedFilter) {
        params.append("applied", appliedFilter);
      }
      if (onlyFailed) {
        params.append("onlyFailed", "true");
      }
      if (uniqueResults) {
        params.append("uniqueResults", "true");
      }
      if (notApplicable) {
        params.append("notApplicable", notApplicable);
      }
      if (confirmedFailure) {
        params.append("confirmedFailure", confirmedFailure);
      }
      if (systemError) {
        params.append("systemError", systemError);
      }
      if (checkoutTechnology) {
        params.append("checkoutTechnology", checkoutTechnology);
      }
      if (merchantName) {
        params.append("merchantName", merchantName);
      }
      if (merchantUrl) {
        params.append("merchantUrl", merchantUrl);
      }
      if (runType) {
        params.append("runType", runType);
      }
      if (publishedStatus) {
        params.append("publishedStatus", publishedStatus);
      }
      if (generateSql) {
        params.append("generateSql", "true");
      }

      const response = await get<Blob>(
        `results/coupons/download/csv?${params.toString()}`,
        { responseType: "blob" }
      );

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);

      const zipFilename = `coupon-results-${timestamp}.zip`;
      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsDownloading(false);
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || "Failed to download coupon results");
      setIsDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Download Coupon Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {(appliedFilter ||
          onlyFailed ||
          uniqueResults ||
          notApplicable ||
          confirmedFailure ||
          systemError ||
          checkoutTechnology ||
          merchantName ||
          merchantUrl ||
          publishedStatus ||
          runType ||
          filterStartDate ||
          filterEndDate) && (
          <Alert variant="info">
            <small>
              <strong>Active Filters:</strong>
              <ul className="mb-0 mt-1">
                {appliedFilter && (
                  <li>Applied: {appliedFilter === "true" ? "Yes" : "No"}</li>
                )}
                {onlyFailed && <li>Only Failed Tests</li>}
                {uniqueResults && <li>Unique Results Only</li>}
                {notApplicable && (
                  <li>
                    Not Applicable: {notApplicable === "true" ? "Yes" : "No"}
                  </li>
                )}
                {confirmedFailure && (
                  <li>
                    Confirmed Failure:{" "}
                    {confirmedFailure === "true" ? "Yes" : "No"}
                  </li>
                )}
                {systemError && (
                  <li>System Error: {systemError === "true" ? "Yes" : "No"}</li>
                )}
                {checkoutTechnology && (
                  <li>Checkout Technology: {checkoutTechnology}</li>
                )}
                {merchantName && <li>Merchant Name: {merchantName}</li>}
                {merchantUrl && <li>Merchant URL: {merchantUrl}</li>}
                {publishedStatus && (
                  <li>
                    Published Status:{" "}
                    {publishedStatus
                      .split(",")
                      .map((s) =>
                        s === "0"
                          ? "Draft"
                          : s === "1"
                            ? "Pending"
                            : s === "2"
                              ? "Live"
                              : s === "3"
                                ? "Suspended"
                                : s
                      )
                      .join(", ")}
                  </li>
                )}
                {filterStartDate && <li>Start Date: {filterStartDate}</li>}
                {filterEndDate && <li>End Date: {filterEndDate}</li>}
              </ul>
            </small>
          </Alert>
        )}

        {!filterStartDate && !filterEndDate && (
          <Form.Group>
            <Form.Label>Results from the last:</Form.Label>
            <Form.Select
              value={hoursBack}
              onChange={(e) => setHoursBack(Number(e.target.value))}
            >
              <option value={1}>1 hour</option>
              <option value={6}>6 hours</option>
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
              <option value={720}>30 days</option>
            </Form.Select>
          </Form.Group>
        )}

        {(filterStartDate || filterEndDate) && (
          <Alert variant="warning">
            <small>Using date range from your current filters.</small>
          </Alert>
        )}

        <Form.Group className="mt-3">
          <Form.Check
            type="checkbox"
            label="Also download SQL script file"
            checked={generateSql}
            onChange={(e) => setGenerateSql(e.target.checked)}
          />
          <Form.Text className="text-muted">
            Always downloads: agentic results CSV + live SQL Server coupon
            matches CSV. When enabled, also downloads the raw SQL script for
            manual execution.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isDownloading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? "Downloading..." : "Download ZIP"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
